import React, { useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import apiFetch from "../../utils/apiFetch";
import { useSnackAlert } from "../../contexts/SnackAlertContext";
import { NAV_SURFACE_DIVIDER } from "./navSurface";

// Each control is one full-width row: the label on the left, its action icon(s)
// on the right. Hoisted so the OneRoster row and the DataRollup row keep the
// same pitch.
const ROW_SX = {
  display: "flex",
  alignItems: "center",
  minHeight: 40,
  borderRadius: 1,
};

// Action icons on the dark nav surface: solid white, no alpha, so they match
// the row label rather than the menu's dimmed glyphs. Set on the button root so
// the glyph, the hover wash and the focus ring all take it.
const ICON_BUTTON_SX = {
  color: "#FFFFFF",
  opacity: 1,
  "& .MuiSvgIcon-root": { color: "#FFFFFF", opacity: 1 },
  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.12)" },
};

// OneRoster import/export and DataRollup sync controls for the nav drawer.
// Gating mirrors the legacy nav chrome: the OneRoster section needs
// the OneRoster (manage) permission and the school's one_roster_export flag, and
// the Upload (SFTP push) button is additionally limited to Florida conference
// schools. Sync DataRollup needs the DataRollup Sync (manage) permission. All of
// those values arrive in appProps, so no extra request is made here.
export default function RosterActions({ appProps, schoolSlug }) {
  const { setSnackAlert } = useSnackAlert();
  const [syncInitiated, setSyncInitiated] = useState(false);

  const school = appProps?.school;
  const permissions = appProps?.permissions ?? {};
  const schoolRef = school?.slug ?? school?.id;

  const canManageOneRoster = Boolean(
    permissions.one_roster && school?.one_roster_export
  );
  const canUploadOneRoster =
    canManageOneRoster && Boolean(school?.is_florida_conference);
  const canSyncDataRollup = Boolean(permissions.datarollup_sync);

  const postAction = async (path, body) => {
    try {
      const response = await apiFetch(path, {
        method: "POST",
        schoolSlug,
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => ({}));
      if (response.ok) {
        setSnackAlert(payload.message || "Request started.", "success");
        return true;
      }
      setSnackAlert(payload.message || "Something went wrong.", "error");
      return false;
    } catch (e) {
      setSnackAlert("Something went wrong.", "error");
      return false;
    }
  };

  const handleDownload = () =>
    postAction("/api/reports/download_one_roster", { school_id: schoolRef });

  const handleUpload = () =>
    postAction("/api/reports/upload_one_roster", { school_id: schoolRef });

  const handleSync = async () => {
    if (syncInitiated) return;
    const ok = await postAction("/api/schools/datarollup_sync", {
      id: schoolRef,
    });
    if (ok) setSyncInitiated(true);
  };

  if (!canManageOneRoster && !canSyncDataRollup) return null;

  return (
    <Box sx={{ px: 2, py: 0.5, borderBottom: NAV_SURFACE_DIVIDER }}>
      {canManageOneRoster && (
        <Box sx={{ ...ROW_SX, justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 15, color: "#FFFFFF" }}>
            OneRoster
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {canUploadOneRoster && (
              <Tooltip title="Upload OneRoster files" arrow>
                <IconButton
                  size="small"
                  aria-label="Upload OneRoster files"
                  onClick={handleUpload}
                  sx={ICON_BUTTON_SX}
                >
                  <UploadFileIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Download OneRoster files" arrow>
              <IconButton
                size="small"
                aria-label="Download OneRoster files"
                onClick={handleDownload}
                sx={ICON_BUTTON_SX}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {canSyncDataRollup && (
        <Box
          sx={{
            ...ROW_SX,
            justifyContent: "space-between",
            opacity: syncInitiated ? 0.5 : 1,
          }}
        >
          <Typography sx={{ fontSize: 15, color: "#FFFFFF" }}>
            {syncInitiated ? "DataRollup sync initiated" : "Sync DataRollup"}
          </Typography>
          {/* aria-disabled rather than disabled: the handler's own guard
              suppresses a repeat request, and the button stays in the
              accessibility tree with the same name once initiated. */}
          <Tooltip title="Sync DataRollup" arrow>
            <IconButton
              size="small"
              aria-label="Sync DataRollup"
              aria-disabled={syncInitiated}
              onClick={handleSync}
              sx={{
                ...ICON_BUTTON_SX,
                cursor: syncInitiated ? "default" : "pointer",
              }}
            >
              <SyncIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}
